import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CACHE_SCOPES,
  CACHE_CONSUMERS,
  CACHE_INVALIDATION_MAP,
  cacheScopeMeta,
  fetchCacheStats,
  invalidateCacheByTag,
  invalidateCacheKey,
  listCacheEntries,
  NEVER_CACHE_RULES,
  purgeExpiredCache,
  setCacheEntry,
  type CoreCacheEntry,
} from '@/lib/coreCache';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function CacheTab({ org }: Props) {
  const [entries, setEntries] = useState<CoreCacheEntry[]>([]);
  const [stats, setStats] = useState({ entries: 0, hits: 0, hitRate: 0, expired: 0, scopes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ key: '', scope: 'catalog', value: '', ttl: 600 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listCacheEntries(org.id), fetchCacheStats(org.id)]);
      setEntries(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const invalidate = async (entry: CoreCacheEntry) => {
    setBusy(entry.id);
    try {
      await invalidateCacheKey(org.id, entry.cache_key.replace(`org:${org.id}:`, ''));
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const purgeExpired = async () => {
    try {
      await purgeExpiredCache(org.id);
      setNotice('Cache expiré purgé.');
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); }
  };

  const invalidateTag = async (tag: string) => {
    try { await invalidateCacheByTag(org.id, tag); setNotice(`Cache « ${tag} » invalidé.`); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); }
  };

  const onAdd = async () => {
    if (!form.key.trim()) { setError('La clé est requise.'); return; }
    let parsed: unknown = {};
    try { parsed = JSON.parse(form.value); } catch { setError('La valeur doit être un JSON valide.'); return; }
    setSaving(true);
    setError('');
    try {
      const ok = await setCacheEntry({ organizationId: org.id, tenantId: org.tenant_id, key: form.key.trim(), scope: form.scope, value: parsed, ttlSeconds: form.ttl });
      if (!ok) { setError(`Le scope « ${cacheScopeMeta(form.scope).label} » ne peut pas être mis en cache.`); return; }
      setForm({ key: '', scope: 'catalog', value: '', ttl: 600 });
      setNotice('Entrée mise en cache.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Écriture impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Cache</span> : une couche pour les données adaptées,
        toujours <span className="font-semibold text-foreground-700">tenant-aware</span>. On ne met jamais en cache
        la disponibilité temps réel, les données privées sensibles ni les autorisations.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-database-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.entries}</p>
          <p className="text-xs text-foreground-500">Entrées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-cursor-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.hits}</p>
          <p className="text-xs text-foreground-500">Hits cumulés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-percent-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.hitRate}%</p>
          <p className="text-xs text-foreground-500">Taux de hit</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-timer-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.expired}</p>
          <p className="text-xs text-foreground-500">Expirées</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-forbid-2-line text-accent-600"></i>Jamais mis en cache</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {NEVER_CACHE_RULES.map((r) => (
            <div key={r.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-medium text-foreground-800 flex items-center gap-2"><i className={`${r.icon} text-accent-600`}></i>{r.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{r.reason}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-links-line text-primary-600"></i>Modules qui consomment le cache</h4>
          <div className="space-y-2">
            {CACHE_CONSUMERS.map((c) => (
              <div key={c.scope} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
                <p className="text-xs font-semibold text-foreground-800 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center"><i className={cacheScopeMeta(c.scope).icon}></i></span>
                  {cacheScopeMeta(c.scope).label}
                  <span className="text-[10px] font-mono text-foreground-400">{c.keyPrefix}*</span>
                </p>
                <p className="text-[11px] text-foreground-500 mt-1">{c.desc}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {c.modules.map((m) => <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{m}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-refresh-line text-accent-600"></i>Invalidation automatique par événement</h4>
          <p className="text-[11px] text-foreground-500 mb-2">Chaque écriture métier émet un événement qui invalide le cache concerné — jamais de donnée périmée.</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {CACHE_INVALIDATION_MAP.map((r) => (
              <div key={r.prefix} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono px-1.5 py-0.5 rounded bg-background-100 text-foreground-700">{r.prefix}*</span>
                <i className="ri-arrow-right-line text-foreground-300"></i>
                <span className="flex flex-wrap gap-1">
                  {r.scopes.map((s) => <span key={s} className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-900">{cacheScopeMeta(s).label}</span>)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-database-2-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Entrées de cache</h4>
          </div>
          <button onClick={purgeExpired} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70">Purger les expirées</button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : entries.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune entrée en cache.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {entries.map((e) => {
              const meta = cacheScopeMeta(e.scope);
              const expired = e.expires_at ? new Date(e.expires_at).getTime() < Date.now() : false;
              return (
                <div key={e.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={meta.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate break-all">{e.cache_key}</p>
                    <p className="text-[11px] text-foreground-400">{meta.label} · TTL {e.ttl_seconds}s · {e.hit_count ?? 0} hits {e.is_private ? '· privé' : ''}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${expired ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'}`}>{expired ? 'Expirée' : 'Valide'}</span>
                  <button onClick={() => invalidate(e)} disabled={busy === e.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Invalider</button>
                  <button onClick={() => invalidateTag((e.tags || [])[0] || 'dashboard')} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer">Invalider par tag</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Mettre une valeur en cache</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input className={INPUT} placeholder="Clé (ex : catalog:products)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
            <select className={INPUT} value={form.scope} onChange={(e) => { const sc = cacheScopeMeta(e.target.value); setForm({ ...form, scope: e.target.value, ttl: sc.ttl }); }}>
              {CACHE_SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}{!s.cacheable ? ' (non cachable)' : ''}</option>)}
            </select>
            <input type="number" min={0} className={INPUT} placeholder="TTL (s)" value={form.ttl} onChange={(e) => setForm({ ...form, ttl: Number(e.target.value) })} />
          </div>
          <textarea className={`${INPUT} h-20 font-mono text-xs`} placeholder='{"value":"exemple"}' value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}></textarea>
          <button onClick={onAdd} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}